import { BaseApiService, ApiResponse } from './baseApi';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';

/** Normalized option for invoice export country dropdown. */
export interface CountryOption {
  /** Stable value for Select (ISO code or `id:<n>` when no code). */
  value: string;
  label: string;
  countryId?: number;
  countryCode?: string;
}

function pickFirstString(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return undefined;
}

function normalizeCountriesPayload(data: unknown): CountryOption[] {
  const raw = Array.isArray(data) ? data : (data as { data?: unknown })?.data;
  if (!Array.isArray(raw)) return [];

  return raw.map((row: Record<string, unknown>, i: number) => {
    const idRaw = row.id ?? row.countryId;
    const id = idRaw != null && !Number.isNaN(Number(idRaw)) ? Number(idRaw) : undefined;

    const code = pickFirstString(
      row.code,
      row.countryCode,
      row.alpha2Code,
      row.iso2,
      row.isoCode,
      row.alpha3Code
    );

    const name =
      pickFirstString(row.name, row.countryName, row.description, row.label) ||
      code ||
      (id != null ? `Country #${id}` : `Country ${i + 1}`);

    const value =
      code && code.length > 0 ? code : id != null && !Number.isNaN(id) ? `id:${id}` : `i:${i}`;

    return {
      value,
      label: name,
      countryId: id,
      countryCode: code,
    };
  });
}

/** Port from GET /common/ports (PortDetailDto). */
export interface PortOption {
  id: number;
  portCode?: string;
  portDescription: string;
  /** Select value (stringified id). */
  value: string;
  /** Display label for dropdown. */
  label: string;
}

function normalizePortsPayload(data: unknown): PortOption[] {
  const raw = Array.isArray(data) ? data : (data as { data?: unknown })?.data;
  if (!Array.isArray(raw)) return [];

  return raw.map((row: Record<string, unknown>, i: number) => {
    const idRaw = row.id;
    const id = idRaw != null && !Number.isNaN(Number(idRaw)) ? Number(idRaw) : i + 1;
    const portCode = pickFirstString(row.portCode, row.port_code);
    const portDescription =
      pickFirstString(row.portDescription, row.port_description, row.description) ||
      portCode ||
      `Port #${id}`;
    const label = portCode && portCode !== portDescription ? `${portDescription} (${portCode})` : portDescription;
    return { id, portCode, portDescription, value: String(id), label };
  });
}

class CommonApiService extends BaseApiService {
  async getCountries(): Promise<CountryOption[]> {
    const data = await this.makeRequest<unknown>(API_ENDPOINTS.COMMON.COUNTRIES, {
      method: HTTP_METHODS.GET,
    });

    // Backend may return ApiResponse<Country[]> or a bare array
    if (data != null && typeof data === 'object' && 'success' in (data as object) && 'data' in (data as object)) {
      const wrapped = data as ApiResponse<unknown>;
      if (wrapped.success && wrapped.data != null) {
        return normalizeCountriesPayload(wrapped.data);
      }
    }

    return normalizeCountriesPayload(data);
  }

  async getPorts(): Promise<PortOption[]> {
    const data = await this.makeRequest<unknown>(API_ENDPOINTS.COMMON.PORTS, {
      method: HTTP_METHODS.GET,
    });

    if (data != null && typeof data === 'object' && 'success' in (data as object) && 'data' in (data as object)) {
      const wrapped = data as ApiResponse<unknown>;
      if (wrapped.success && wrapped.data != null) {
        return normalizePortsPayload(wrapped.data);
      }
    }

    return normalizePortsPayload(data);
  }
}

export const commonApiService = new CommonApiService();
